import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';

const TABS = ['CV', 'C. Letter', 'Tips', 'Insights'];

export default function ResultsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [activeTab, setActiveTab] = useState(0);
  const results = state?.results;

  if (!results) {
    navigate('/app');
    return null;
  }

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (e) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Copied to clipboard!');
    }
  };

  const handleDownload = async () => {
    try {
      const formData = new FormData();
      formData.append('content', results.tailored_cv);
      formData.append('title', 'Tailored CV');

      const response = await fetch('https://api.theapplyedge.com/api/v1/download-docx', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Download failed');
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'ApplyEdge_CV.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed: ' + e.message);
    }
  };

  const tabContent = [
    results.tailored_cv,
    results.cover_letter,
    results.talking_points,
    results.insights,
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0D0D1A' }}>

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 40px', borderBottom: '1px solid #2A2A4A'
      }}>
        <div onClick={() => navigate('/app')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <img src="/app_icon.png" alt="ApplyEdge" style={{ width: 34, height: 34, borderRadius: 9 }} />
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 18 }}>ApplyEdge</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ color: '#8888AA', fontSize: 13 }}>{user?.displayName}</div>
          <button onClick={handleSignOut} style={{
            background: 'transparent', border: '1px solid #2A2A4A',
            borderRadius: 8, padding: '6px 14px', color: '#8888AA',
            fontSize: 13, cursor: 'pointer'
          }}>Sign Out</button>
        </div>
      </nav>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>

        {/* ATS Score */}
        <div style={{
          display: 'flex', gap: 20, marginBottom: 32, flexWrap: 'wrap'
        }}>
          <div style={{
            background: '#1A1A2E', border: '1px solid #2A2A4A',
            borderRadius: 16, padding: '24px 32px', flex: 1, textAlign: 'center'
          }}>
            <div style={{ color: '#8888AA', fontSize: 13, marginBottom: 8 }}>ATS Score Before</div>
            <div style={{
              fontSize: 48, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
              color: '#FF6B6B'
            }}>{results.ats_before}%</div>
          </div>
          <div style={{
            background: '#1A1A2E', border: '1px solid #2A2A4A',
            borderRadius: 16, padding: '24px 32px', flex: 1, textAlign: 'center'
          }}>
            <div style={{ color: '#8888AA', fontSize: 13, marginBottom: 8 }}>ATS Score After</div>
            <div style={{
              fontSize: 48, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
              color: '#00D4AA'
            }}>{results.ats_after}%</div>
          </div>
          <div style={{
            background: '#1A1A2E', border: '1px solid #2A2A4A',
            borderRadius: 16, padding: '24px 32px', flex: 1, textAlign: 'center'
          }}>
            <div style={{ color: '#8888AA', fontSize: 13, marginBottom: 8 }}>Improvement</div>
            <div style={{
              fontSize: 48, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
              color: '#0F7DFF'
            }}>+{results.ats_after - results.ats_before}%</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, background: '#1A1A2E',
          borderRadius: 12, padding: 4, marginBottom: 20, width: 'fit-content'
        }}>
          {TABS.map((tab, i) => (
            <button key={i} onClick={() => setActiveTab(i)} style={{
              padding: '8px 20px', borderRadius: 9, border: 'none',
              background: activeTab === i ? 'linear-gradient(135deg, #0F7DFF, #00D4AA)' : 'transparent',
              color: activeTab === i ? '#fff' : '#8888AA',
              fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14,
              cursor: 'pointer'
            }}>{tab}</button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          background: '#1A1A2E', border: '1px solid #2A2A4A',
          borderRadius: 16, padding: 24
        }}>
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: 'Inter, sans-serif', fontSize: 14,
            color: '#FFFFFF', lineHeight: 1.7, minHeight: 300
          }}>
            {tabContent[activeTab]}
          </pre>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <button onClick={() => handleCopy(tabContent[activeTab])} style={{
            flex: 1, padding: '12px', background: '#1A1A2E',
            border: '1px solid #2A2A4A', borderRadius: 12,
            color: '#fff', fontFamily: 'Inter, sans-serif',
            fontWeight: 600, fontSize: 14, cursor: 'pointer'
          }}>📋 Copy</button>
          {activeTab === 0 && (
            <button onClick={handleDownload} style={{
              flex: 1, padding: '12px',
              background: 'linear-gradient(135deg, #0F7DFF, #00D4AA)',
              border: 'none', borderRadius: 12,
              color: '#fff', fontFamily: 'Inter, sans-serif',
              fontWeight: 600, fontSize: 14, cursor: 'pointer'
            }}>⬇ Download DOCX</button>
          )}
          <button onClick={() => navigate('/app')} style={{
            flex: 1, padding: '12px', background: '#1A1A2E',
            border: '1px solid #2A2A4A', borderRadius: 12,
            color: '#fff', fontFamily: 'Inter, sans-serif',
            fontWeight: 600, fontSize: 14, cursor: 'pointer'
          }}>🔄 Optimise Another</button>
        </div>

      </div>
    </div>
  );
}